import { isEmpty, isNil } from 'lodash';

/**
 * Marks text that came from speech recognition rather than a keyboard.
 *
 * Models are trained on tagged input and read a tag like this as licence to be forgiving about
 * homophones and mishearings — "unit for" for "unit four", a name spelled the way it sounded. It
 * costs a handful of tokens and asks nothing of the gateway, which passes the prompt through.
 */
const OPEN_TAG = '<user-dictation>';
const CLOSE_TAG = '</user-dictation>';

/** What the tags cost when they are used, for the prompt budget to account for. */
export const DICTATION_TAG_CHARS = OPEN_TAG.length + CLOSE_TAG.length;

/**
 * Wraps the dictated part of a prompt, or the whole prompt when that part can no longer be found.
 *
 * The precise span is worth marking where it survives: someone who typed "unit 4" and dictated the
 * rest wrote that number deliberately, and a model told the whole turn was heard rather than typed
 * may well decide the digit was a mishearing.
 *
 * Where the user has since edited what was dictated, the exact span is gone — offsets shift and
 * there is nothing honest left to point at. Falling back to the whole prompt is deliberate, and the
 * opposite of what it first looks like: an edit fixes the words the user noticed, not the ones they
 * did not, so most of a long transcription is still raw recognition. Dropping the tag there would
 * lose the signal at exactly the moment it is still needed.
 */
export function withDictationTag(prompt: string, dictatedText: string | undefined): string {
  if (isNil(dictatedText) || isEmpty(dictatedText.trim())) {
    return prompt;
  }
  const dictated = dictatedText.trim();
  const at = prompt.indexOf(dictated);
  if (at === -1) {
    return `${OPEN_TAG}${prompt}${CLOSE_TAG}`;
  }
  const before = prompt.slice(0, at);
  const after = prompt.slice(at + dictated.length);
  return `${before}${OPEN_TAG}${dictated}${CLOSE_TAG}${after}`;
}
